import { IUser } from '~/src/dash/api/console-api';
import { ELiveDataMetrics } from '~/src/dash/api/live_data/models';
import { TargetedMetricCondition } from '~/src/dash/api/monitors/monitors_utils';
import { parseNumber, parseString, transformModel } from '~/src/dash/api/resource-transforms';
import { EBool } from '~/src/dash/api/types/bool';
import {
  IMonitorNotifierEmailData,
  IMonitorNotifierEmailDataForHistory,
} from '~/src/dash/components/monitor/monitor_notifications/notifier_email/notifier_email';
import { IMonitorNotifierOpsgenieDataForHistory } from '~/src/dash/components/monitor/monitor_notifications/notifier_opsgenie/notifier_opsgenie';
import {
  IMonitorNotifierSlackData,
  IMonitorNotifierSlackDataForHistory,
} from '~/src/dash/components/monitor/monitor_notifications/notifier_slack/notifier_slack';
import {
  IMonitorNotifierURLData,
  IMonitorNotifierURLDataForHistory,
} from '~/src/dash/components/monitor/monitor_notifications/notifier_url/notifier_url';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
export interface IMonitorAlertThresholds {
  AlertThreshold: number;
  WarningThreshold: number;
  AlertRecoveryThreshold: number;
  WarningRecoveryThreshold: number;
  IsPercentage: boolean;
}

export interface IMonitorNotificationSettings {
  ID: number;
  CreatedAt?: Date;

  Active: EBool;

  MonitorID: number;

  TriggerType: EMonitorTriggerType[];
  Provider: EMonitorNotificationProvider;
  RenotificationEnabled: EBool;

  Data?: IMonitorNotifierURLData | IMonitorNotifierEmailData | IMonitorNotifierSlackData;
}

export interface IMonitorNotificationHistory extends IMonitorNotificationSettings {
  AlertID: number;
  TriggeredType: EMonitorTriggerType;
  IsTest: EBool;
  SentData?:
    | IMonitorNotifierURLDataForHistory
    | IMonitorNotifierEmailDataForHistory
    | IMonitorNotifierSlackDataForHistory
    | IMonitorNotifierOpsgenieDataForHistory;
}

export interface IMonitorAlertHistory {
  ID: number;
  MonitorID: number;
  Active: number;

  CreatedAt: string;
  TriggerType: EMonitorTriggerType;
  Value: number;
  LimitAtThisMoment: number;
  AcknowledgedAt: string;
  AcknowledgedByUserID: { Int64: number; Valid: boolean };
  AcknowledgedByUserName: string;
  User: IUser;

  // conditions from monitor
  TargetedObjectType: EMonitorObjectType;
  TargetedObject: string;
  TargetedMetric: ELiveDataMetrics;
  MetricDirection: number;
  ThresholdComputation: number;
  AlertTimeframe: number;
  PercentagePeriod: number;
  PercentageGranularity: number;

  IsTest: EBool;

  // only used in the ui to display the names and ids of alerting objects
  listItems: IListItem[];
}

export interface IMonitor {
  ID: number;
  CreatedAt?: Date;
  PublisherID: number;
  Active: EBool;
  EnableAutoDisable: EBool;
  EnableTargetAllActive: EBool;
  EnableAcknowledgeAlerts: EBool;
  MonitorType: EMonitorType;
  Name: string;
  IsSuperAdmin: EBool;
  CreatedByPublisherID: number;
  CreatedByUserID: number;
  MutedUntil?: Date;

  TargetedObjectType: EMonitorObjectType;
  TargetedObject: string;
  TargetedMetric: ELiveDataMetrics;
  MetricDirection: number;
  ThresholdComputation: number;
  AlertTimeframe: EMonitorAlertTimeframe;
  AveragePeriod: EMonitorAlertTimeframe;
  PercentagePeriod: number;
  PercentageGranularity: number;
  AlertThresholds: IMonitorAlertThresholds;

  AlertHistory: IMonitorAlertHistory[];

  NotificationsSettings: IMonitorNotificationSettings[];
  NotificationsHistory: IMonitorNotificationHistory[];

  // custom metrics
  MonitorCustomMetricID: number;
  MonitorCustomMetric: IMonitorCustomMetric; // parse
  // for pill switcher
  targetedMetricType: TargetedMetricCondition;
  monitorCustomMetrics: IMonitorCustomMetric[];
  monitorDefinedMetricsList: IMenuList;
  monitorCustomMetricsList: IMenuList;
  TargetedPublisherIDs: string;
  MonitorLookbackType: EMonitorLookbackType;
}

export interface IMonitorCustomMetric {
  ID: number;
  Name: string;
  CustomMetricA: ELiveDataMetrics;
  CustomMetricB: ELiveDataMetrics;
  MetricFormula: string;
  PublisherID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
}

export const transformMonitorCustomMetric = transformModel<IMonitorCustomMetric>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Name: parseString(['Name']),
  CustomMetricA: parseNumber(['CustomMetricA']),
  CustomMetricB: parseNumber(['CustomMetricB']),
  MetricFormula: parseString(['MetricFormula']),
  PublisherID: parseNumber(['PublisherID']),
});

interface INewAlertsMap {
  [key: number]: IMonitorAlertHistory[];
}

export interface IMaxMinMonitorValue {
  max: number;
  min: number;
  currentValuesMap: Record<number, number>;
  previousValuesMap: Record<number, number>;
  resultValuesMap: Record<number, number>;
  newAlertsMap: INewAlertsMap;
}

export enum EMonitorObjectType {
  MonitorObjectType_UNKNOWN = 0,
  MonitorObjectType_BIDDER = 1,
  MonitorObjectType_SITE_BIDDER = 2,
  MonitorObjectType_SITE = 3,
  MonitorObjectType_DEMAND_SOURCE = 4,
  MonitorObjectType_BIDDER_GROUP = 5,
  MonitorObjectType_PUBLISHER = 6,
  MonitorObjectType_CAMPAIGN = 7,
  MonitorObjectType_CHANNEL_GROUP = 8,
  MonitorObjectType_NETWORK = 9,
  MonitorObjectType_CHECKPOINT = 10,
}

export enum EMonitorType {
  MonitorType_SINGLE = 0,
  MonitorType_MULTI = 1,
}

export const MultiMonitorTypeObjectTypes = [
  EMonitorObjectType.MonitorObjectType_BIDDER,
  EMonitorObjectType.MonitorObjectType_SITE,
  EMonitorObjectType.MonitorObjectType_BIDDER_GROUP,
  EMonitorObjectType.MonitorObjectType_CAMPAIGN,
  EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP,
  EMonitorObjectType.MonitorObjectType_DEMAND_SOURCE,
  EMonitorObjectType.MonitorObjectType_NETWORK,
];

export const EnableTargetAllActiveObjectTypes = [
  EMonitorObjectType.MonitorObjectType_BIDDER,
  EMonitorObjectType.MonitorObjectType_SITE,
  EMonitorObjectType.MonitorObjectType_CAMPAIGN,
  EMonitorObjectType.MonitorObjectType_NETWORK,
];

export const EnableAutoDisableObjectTypes = [
  EMonitorObjectType.MonitorObjectType_BIDDER,
  EMonitorObjectType.MonitorObjectType_SITE,
  EMonitorObjectType.MonitorObjectType_BIDDER_GROUP,
  EMonitorObjectType.MonitorObjectType_CAMPAIGN,
  EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP,
];

export enum EMonitorMetricDirection {
  MonitorMetricDirection_ABOVE = 0,
  MonitorMetricDirection_UNDER = 1,
}

export enum EMonitorThresholdComputationType {
  MonitorThresholdComputationType_SUM = 0,
  MonitorThresholdComputationType_AVG = 1,
  MonitorThresholdComputationType_PERCENTAGE = 2,
}

export const EMonitorMetricsWithNoTimeframe = new Set<ELiveDataMetrics>([
  ELiveDataMetrics.CAMPAIGN_PACING,
]);

export const EMonitorMetricsOnlyValueAlertThreshold = new Set<ELiveDataMetrics>([
  ELiveDataMetrics.CAMPAIGN_PACING,
]);

export const EMonitorObjectTypesWithoutCustomMetricType = new Set<EMonitorObjectType>([
  EMonitorObjectType.MonitorObjectType_CHECKPOINT,
]);

export enum EMonitorAlertTimeframe {
  MonitorAlertTimeframe_1H = 0,
  MonitorAlertTimeframe_2H = 1,
  MonitorAlertTimeframe_4H = 2,
  MonitorAlertTimeframe_6H = 3,
  MonitorAlertTimeframe_12H = 4,
  MonitorAlertTimeframe_24H = 5,
  MonitorAlertTimeframe_15MIN = 6,
  MonitorAlertTimeframe_30MIN = 7,
  MonitorAlertTimeframe_Past48H_To_24H = 8,
  MonitorAlertTimeframe_NoTimeframe = 9,
}

export enum EMonitorLookbackType {
  MonitorLookbackType_NONE = 0,
  MonitorLookbackType_CONSECUTIVE = 1,
  MonitorLookbackType_PREVIOUS_DAY = 2,
  MonitorLookbackType_PREVIOUS_WEEK = 3,
}

export enum EMonitorTriggerType {
  MonitorTriggerType_ALERT = 0,
  MonitorTriggerType_WARNING = 1,
  MonitorTriggerType_ALERT_RECOVERY = 2,
  MonitorTriggerType_WARNING_RECOVERY = 3,
  MonitorTriggerType_SHUTDOWN = 4,
}

export enum EMonitorNotificationProvider {
  MonitorNotificationProvider_URL = 0,
  MonitorNotificationProvider_EMAIL = 1,
  MonitorNotificationProvider_SLACK = 2,
  MonitorNotificationProvider_OPSGENIE = 3,
}

export const getNotificationProvider = (provider: EMonitorNotificationProvider): string => {
  if (provider === EMonitorNotificationProvider.MonitorNotificationProvider_URL) {
    return 'URL';
  } else if (provider === EMonitorNotificationProvider.MonitorNotificationProvider_EMAIL) {
    return 'EMAIL';
  } else if (provider === EMonitorNotificationProvider.MonitorNotificationProvider_SLACK) {
    return 'SLACK';
  } else if (provider === EMonitorNotificationProvider.MonitorNotificationProvider_OPSGENIE) {
    return 'OPSGENIE';
  }
  return 'UNKNOWN';
};
